import type { DataGridColumn } from "@/components/kit";

import { Button } from "@heroui/react";
import { Segment } from "@heroui-pro/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  ChevronsUpDown,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import ProductFormModal from "../modal/add-edit";
import { ChangePrimaryScrapedProductModal } from "../modal/change-primary";
import { ProductDeleteModal } from "../modal/delete";
import { ProductViewModal } from "../modal/view";

import {
  ActionsCell,
  LinkedCell,
  NamesCell,
  PriceCell,
  ProductCell,
  StockCell,
} from "./cells";
import { productColumns } from "./columns";
import { countActiveFilters, useProductFilterControls } from "./filters";
import { ImageGalleryModal } from "./image-gallery-modal";

import {
  AppliedFilters,
  BulkBar,
  ColumnsMenu,
  DataTable,
  notify,
  SearchBox,
} from "@/components/kit";
import { PRODUCT_KEY } from "@/hooks/api/product/keys";
import { useAddProduct } from "@/hooks/api/product/useAddProduct";
import {
  useEditProduct,
  useUpdateProductStock,
} from "@/hooks/api/product/useEditProduct";
import { useDebounce } from "@/hooks/useDebounce";
import {
  AddProduct,
  PaginatedResponse,
  Product,
  ProductFilters,
  UpdateProductStock,
} from "@/types";

interface ProductListTableProps {
  items: Product[];
  page: number;
  totalProducts: number;
  totalPage: number;
  changePage: (page: number) => void;
  isLoading: boolean;
  limit: number;
  onLimitChange: (limit: number) => void;
  filters: ProductFilters;
  setFilter: <K extends keyof ProductFilters>(
    key: K,
    value: ProductFilters[K],
  ) => void;
  setFilters: (filters: Partial<ProductFilters>) => void;
  clearFilters: () => void;
  isAddOpen: boolean;
  onAddOpenChange: (open: boolean) => void;
}

/** The table's columns by the name the Columns menu and the sort keys use. */
const COLUMN_LABEL = Object.fromEntries(
  productColumns.map((c) => [c.key, c.label]),
);

export const ProductListTable = ({
  items,
  page,
  totalProducts,
  changePage,
  isLoading,
  limit,
  onLimitChange,
  filters,
  setFilter,
  setFilters,
  clearFilters,
  isAddOpen,
  onAddOpenChange,
}: ProductListTableProps) => {
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(
    () => new Set(productColumns.map((c) => c.key)),
  );
  const [selectedRows, setSelectedRows] = useState<Set<string | number>>(
    new Set(),
  );
  const [selectedEditProduct, setSelectedEditProduct] =
    useState<Product | null>(null);
  const [selectedViewProduct, setSelectedViewProduct] =
    useState<Product | null>(null);
  const [productToChangePrimary, setProductToChangePrimary] =
    useState<Product | null>(null);
  const [selectedImage, setSelectedImage] = useState<{
    name: string;
    images: string[];
  } | null>(null);

  const {
    mutate: updateProductStock,
    isPending: isProductStockUpdating,
    variables: productStockUpdateVariables,
  } = useUpdateProductStock();
  const {
    mutate: addProduct,
    isPending: isAddingProduct,
    isError: isProductAddError,
    error: productAddError,
    reset: resetAddProductMutation,
  } = useAddProduct();
  const {
    mutate: editProduct,
    isPending: isEditingProduct,
    isError: isProductEditError,
    error: productEditError,
    reset: resetEditProductMutation,
  } = useEditProduct();

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [localSearchTerm, setLocalSearchTerm] = useState(
    () => filters.searchTerm || "",
  );
  const debouncedSearchTerm = useDebounce(localSearchTerm, 500);

  const handleSortChange = (sortKey: string) => {
    const currentSorts = filters.sort || [];
    const existingIndex = currentSorts.findIndex((s) => s.key === sortKey);
    let newSorts;

    if (existingIndex >= 0) {
      const current = currentSorts[existingIndex];

      if (current.direction === "asc") {
        // asc -> desc
        newSorts = currentSorts.map((s, i) =>
          i === existingIndex ? { ...s, direction: "desc" as const } : s,
        );
      } else {
        // desc -> remove
        newSorts = currentSorts.filter((_, i) => i !== existingIndex);
      }
    } else {
      // New sort: add as ascending
      newSorts = [...currentSorts, { key: sortKey, direction: "asc" as const }];
    }

    setFilters({ sort: newSorts.length > 0 ? newSorts : undefined });
  };

  const handleClearSort = useCallback(() => {
    setFilters({ sort: undefined });
  }, [setFilters]);

  const handleClearAllFilters = useCallback(() => {
    clearFilters();
    setLocalSearchTerm("");
  }, [clearFilters]);

  const filterControls = useProductFilterControls(filters, setFilters);

  const handleProductStockChange = (data: UpdateProductStock) => {
    updateProductStock(data, {
      onError: () => {
        notify.error("Failed to update product stock");
      },
      onSuccess: () => {
        const caches = queryClient.getQueryCache();
        const productCaches = caches
          .getAll()
          .filter((cache) => cache.queryKey.includes(PRODUCT_KEY));

        productCaches.forEach((productCache) => {
          const cacheProducts = productCache.state
            .data as PaginatedResponse<Product>;

          cacheProducts.data = cacheProducts.data.map((cacheProduct) => {
            if (cacheProduct.id === data.id) {
              return {
                ...cacheProduct,
                inStock: data.inStock,
              };
            }

            return cacheProduct;
          });

          productCache.setData(cacheProducts);
        });

        const product = items.find((item) => item.id == data.id);

        if (product) {
          product.inStock = data.inStock;
        }
      },
    });
  };

  const handleClearSelection = useCallback(() => {
    setSelectedRows(new Set());
  }, []);

  const invalidateProducts = () => {
    queryClient.refetchQueries({
      predicate(query) {
        return query.queryKey.includes(PRODUCT_KEY);
      },
    });
  };

  const handleCreate = (data: AddProduct) => {
    addProduct(data, {
      onSuccess: () => {
        notify.success("Product added successfully");
        toggleAddForm();
        invalidateProducts();
      },
    });
  };

  const handleEdit = (data: AddProduct) => {
    editProduct(
      {
        id: selectedEditProduct!.id!,
        ...data,
      },
      {
        onSuccess: () => {
          notify.success("Product updated successfully");
          setSelectedEditProduct(null);
          invalidateProducts();
        },
      },
    );
  };

  useEffect(() => {
    setSelectedRows(new Set());
  }, [items]);

  // Sync debounced search term to URL
  useEffect(() => {
    if (debouncedSearchTerm !== filters.searchTerm) {
      setFilter("searchTerm", debouncedSearchTerm);
    }
  }, [debouncedSearchTerm]);

  // Sync local search term from URL (for browser back/forward)
  useEffect(() => {
    if (filters.searchTerm !== localSearchTerm && filters.searchTerm === "") {
      setLocalSearchTerm("");
    }
  }, [filters.searchTerm]);

  const toggleAddForm = () => onAddOpenChange(!isAddOpen);
  const toggleDeleteModal = () => setIsDeleteModalVisible((prev) => !prev);

  const sortRank = (sortKey: string) => {
    const index = (filters.sort || []).findIndex((s) => s.key === sortKey);

    return index >= 0
      ? { rank: index + 1, direction: filters.sort![index].direction }
      : null;
  };

  const header = (key: string) => {
    const column = productColumns.find((c) => c.key === key)!;
    const sorted = column.sortKey ? sortRank(column.sortKey) : null;

    const label =
      key === "position" ? (
        <>
          <span aria-hidden="true">#</span>
          <span className="sr-only">Position</span>
        </>
      ) : (
        column.label
      );

    return () => (
      <span className="inline-flex items-center gap-1">
        {label}
        {sorted ? (
          <span className="text-accent inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums">
            {sorted.rank}
            {sorted.direction === "asc" ? (
              <ArrowUp aria-hidden="true" className="size-3" />
            ) : (
              <ArrowDown aria-hidden="true" className="size-3" />
            )}
          </span>
        ) : column.sortKey ? (
          <ChevronsUpDown aria-hidden="true" className="text-muted size-3" />
        ) : null}
      </span>
    );
  };

  const allColumns = useMemo<DataGridColumn<Product>[]>(() => {
    const sortable = (key: string) =>
      Boolean(productColumns.find((c) => c.key === key)?.sortable);
    const cats = (p: Product) =>
      p.categories ?? (p.category ? [p.category] : []);
    const subs = (p: Product) =>
      p.subCategories ?? (p.subCategory ? [p.subCategory] : []);
    const ctrs = (p: Product) =>
      p.countries ?? (p.country ? [p.country] : []);

    return [
      {
        id: "product",
        header: header("product"),
        allowsSorting: sortable("product"),
        isRowHeader: true,
        width: "1fr",
        minWidth: 170,
        cell: (product) => (
          <ProductCell
            product={product}
            onImageClick={() =>
              setSelectedImage({
                name: product.name,
                images: product.images || [],
              })
            }
          />
        ),
      },
      {
        id: "position",
        header: header("position"),
        allowsSorting: sortable("position"),
        width: 60,
        cell: (product) => (
          <span className="text-muted tabular-nums">{product.position}</span>
        ),
      },
      {
        id: "price",
        header: header("price"),
        allowsSorting: sortable("price"),
        width: 160,
        cell: (product) => <PriceCell product={product} />,
      },
      {
        id: "category",
        header: header("category"),
        allowsSorting: sortable("category"),
        width: 130,
        cell: (product) => (
          <NamesCell primary={cats(product)} secondary={subs(product)} />
        ),
      },
      {
        id: "brand",
        header: header("brand"),
        allowsSorting: sortable("brand"),
        width: 130,
        cell: (product) => (
          <NamesCell
            primary={product.brand ? [product.brand] : []}
            secondary={ctrs(product)}
          />
        ),
      },
      {
        id: "supplier",
        header: header("supplier"),
        width: 115,
        cell: (product) => (
          <span className="block max-w-[100px] truncate text-sm">
            {product.supplier?.name ?? <span className="text-muted">-</span>}
          </span>
        ),
      },
      {
        id: "linked",
        header: header("linked"),
        width: 100,
        cell: (product) => <LinkedCell product={product} />,
      },
      {
        id: "stock",
        header: header("stock"),
        width: 60,
        cell: (product) => (
          <StockCell
            isInStock={product.inStock}
            isLoading={
              isProductStockUpdating &&
              productStockUpdateVariables?.id === product.id
            }
            onChange={(isInStock) =>
              handleProductStockChange({ id: product.id, inStock: isInStock })
            }
          />
        ),
      },
      {
        id: "actions",
        header: <span className="sr-only">Actions</span>,
        width: 48,
        align: "end",
        cell: (product) => (
          <ActionsCell
            product={product}
            onChangePrimary={() => setProductToChangePrimary(product)}
            onEdit={() => setSelectedEditProduct(product)}
            onView={() => setSelectedViewProduct(product)}
            onViewLinked={
              product.scrapedProduct
                ? () =>
                    navigate(
                      `/scraped/products?name=${encodeURIComponent(product.scrapedProduct!.name)}`,
                    )
                : undefined
            }
          />
        ),
      },
    ];
  }, [
    filters.sort,
    isProductStockUpdating,
    productStockUpdateVariables,
    navigate,
  ]);

  const columns = allColumns.filter(
    (c) => c.id === "actions" || visibleColumns.has(c.id),
  );

  const activeFilterCount = countActiveFilters(filters);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="isolate flex flex-wrap items-center gap-3">
        <Segment
          aria-label="Stock status"
          selectedKey={
            filters.checkInStock === undefined
              ? "any"
              : String(filters.checkInStock)
          }
          onSelectionChange={(key) =>
            setFilters({
              checkInStock: key === "any" ? undefined : key === "true",
            })
          }
        >
          <Segment.Item id="any">All</Segment.Item>
          <Segment.Item id="true">
            <Segment.Separator />
            In stock
          </Segment.Item>
          <Segment.Item id="false">
            <Segment.Separator />
            Out of stock
          </Segment.Item>
        </Segment>
        <SearchBox
          className="w-full max-w-xs"
          placeholder="Search products"
          value={localSearchTerm}
          onChange={setLocalSearchTerm}
        />
        <div className="ms-auto flex items-center gap-2">
          <ColumnsMenu
            columns={allColumns
              .filter((c) => c.id !== "actions")
              .map((c) => ({ id: c.id, label: COLUMN_LABEL[c.id] }))}
            visible={visibleColumns}
            onChange={setVisibleColumns}
          />
          {filterControls.popover}
        </div>
      </div>

      {filters.sort?.length || filterControls.applied.length > 0 ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <AppliedFilters
            filters={filterControls.applied}
            onClearAll={handleClearAllFilters}
            onRemove={filterControls.remove}
          />
          {filters.sort?.length ? (
            <div className="flex items-center gap-2">
              <span className="text-muted text-sm">
                Sorted by{" "}
                {filters.sort
                  .map(
                    (s) =>
                      productColumns.find((c) => c.sortKey === s.key)?.label ??
                      s.key,
                  )
                  .join(", ")}
              </span>
              <Button size="sm" variant="ghost" onPress={handleClearSort}>
                Clear Sort
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      <DataTable
        compact
        columns={columns}
        minTableWidth={1000}
        empty={{
          title: "No products found",
          description:
            activeFilterCount > 0 || filters.searchTerm || filters.checkInStock !== undefined
              ? "Try a different search or clear the filters."
              : "Add the first product to get started.",
          action:
            activeFilterCount > 0 || filters.searchTerm || filters.checkInStock !== undefined ? (
              <Button
                size="sm"
                variant="secondary"
                onPress={handleClearAllFilters}
              >
                Clear filters
              </Button>
            ) : undefined,
        }}
        getRowId={(product) => product.id}
        isLoading={isLoading && items.length === 0}
        label="Products"
        pagination={{
          page,
          pageSize: limit,
          total: totalProducts,
          itemLabel: "products",
          onPageChange: changePage,
          onPageSizeChange: onLimitChange,
        }}
        rows={items}
        selection={{
          keys: selectedRows,
          onChange: setSelectedRows,
          allKeys: items.map((item) => item.id),
        }}
        onSortChange={(descriptor) => {
          const column = productColumns.find(
            (c) => c.key === descriptor.column,
          );

          if (column?.sortKey) handleSortChange(column.sortKey);
        }}
      />

      <BulkBar count={selectedRows.size} onClear={handleClearSelection}>
        <Button size="sm" variant="danger-soft" onPress={toggleDeleteModal}>
          <Trash2 className="size-4" />
          Delete
        </Button>
      </BulkBar>

      {selectedImage && (
        <ImageGalleryModal
          images={selectedImage.images}
          isOpen={!!selectedImage}
          name={selectedImage.name}
          onClose={() => setSelectedImage(null)}
        />
      )}

      <ProductFormModal
        error={productAddError}
        isError={isProductAddError}
        isLoading={isAddingProduct}
        isOpen={isAddOpen}
        resetMutation={resetAddProductMutation}
        onClose={toggleAddForm}
        onSave={handleCreate}
      />
      <ProductFormModal
        error={productEditError}
        isError={isProductEditError}
        isLoading={isEditingProduct}
        isOpen={Boolean(selectedEditProduct)}
        product={selectedEditProduct}
        resetMutation={resetEditProductMutation}
        onClose={() => setSelectedEditProduct(null)}
        onSave={handleEdit}
      />
      <ProductViewModal
        isOpen={Boolean(selectedViewProduct)}
        product={selectedViewProduct}
        onClose={() => setSelectedViewProduct(null)}
      />
      <ProductDeleteModal
        isOpen={isDeleteModalVisible}
        products={items.filter((item) => selectedRows.has(item.id))}
        onClose={toggleDeleteModal}
      />
      <ChangePrimaryScrapedProductModal
        isOpen={Boolean(productToChangePrimary)}
        product={productToChangePrimary}
        onClose={() => setProductToChangePrimary(null)}
      />
    </div>
  );
};
